import { Link, useLocation } from '@reach/router';
import React, { useEffect, useState } from 'react';
import queryString from 'query-string';
import { formatNumber, formatPrice } from 'utils/formating';
import PackageCard from './PackageCard';
import { useFetchPackages } from 'hooks/useFetchPackages';
import {
  IPriceInfo,
  IPromoCode,
  Package,
  PackageGroup,
  PackagesList,
  Subjects,
} from 'pages/newSubscription/models';
import { NewSubscriptionApi } from 'api/newSubscriptionApi';
import FormGeneralError from 'components/formComponents/FormGeneralError';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectSelectedPackages,
  setPrice,
  setValidPromoCode,
  updateSelectedPackages,
} from 'reducers/misc';
import { Spinner } from 'components/spinner/Spinner';
import { AppPath, ERROR_MESSAGE, PUBLIC_SITE_URL } from 'appConstants';
import { SubscribeApi } from 'api/subscribeApi';
import ErrorMessage from 'components/errorMessage/ErrorMessage';
import { getVATAndPriceWithVAT } from 'helpers/priceCalculations';

type ChoosePackageFormProps = {
  onSubmit: () => void;
};

export const ChoosePackageForm: React.FC<ChoosePackageFormProps> = ({
  onSubmit,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const pathname = location.pathname;
  const queryParams = location
    ? queryString.parse(location.search, { decode: false })
    : undefined;

  const selectedPackages = useSelector(selectSelectedPackages);

  const [allPackages, setAllPackages] = useState<PackagesList>([]);
  const [amountOffDiscount, setAmountOffDiscount] = useState<number | null>();
  const [promoCode, setPromoCode] = useState<IPromoCode>({
    value: '',
    error: '',
  });
  const [priceInfo, setPriceInfo] = useState<IPriceInfo>({
    price: 0,
    vat: 0,
    total: 0,
    discount: 0,
    appliedDiscountType: '',
  });

  const { packages, vatRate, amountOff, error } = useFetchPackages(
    location.pathname
  );

  useEffect(() => {
    if (!packages) return;
    setAmountOffDiscount(amountOff);
    setAllPackages(packages);
  }, [packages]);

  useEffect(() => {
    if (!packages) return;
    //preselect subscribed free trial packages
    const freeTrialPackages = packages
      .map((p: any) =>
        p.subjects.map((subject: any) =>
          subject.subscriptions.map((subscription: any) => {
            if (subscription.isFreeTrial) {
              return {
                ...subscription,
                ageGroup: p.name,
                subject: subject.name,
              };
            }
          })
        )
      )
      .flat()
      .flat()
      .filter((item: Package | undefined) => item);

    if (freeTrialPackages && freeTrialPackages.length > 0) {
      dispatch(updateSelectedPackages(freeTrialPackages));
    }

    //the subscribe url will include a param when the user has selected a package
    //from the pricing page and it gets redirected to the subscribe page
    const packageId = queryParams ? queryParams.packageId : null;
    if (!packageId) return;
    const paramPackage = packages
      .map((p: any) =>
        p.subjects.map((subject: any) =>
          subject.subscriptions.map((subscription: any) => {
            if (subscription.packageID == packageId) {
              return {
                ...subscription,
                ageGroup: p.name,
                subject: subject.name,
              };
            }
          })
        )
      )
      .flat()
      .flat()
      .filter((item: Package | undefined) => item);

    if (paramPackage && paramPackage.length > 0) {
      dispatch(updateSelectedPackages(paramPackage));
    }
  }, [packages]);

  useEffect(() => {
    if (!vatRate) return;
    const [price, total, vat, discount, appliedDiscountType] =
      calculatePrice(vatRate);

    setPriceInfo({ price, vat, total, discount, appliedDiscountType });
  }, [selectedPackages]);

  const selectPackage = (p: Package) => {
    const packageList = selectedPackages;
    const includesPackage = packageList.find(
      (i: Package) => i.packageID === p.packageID
    );

    if (includesPackage) {
      /*Clear unselected packages*/
      const filteredPackageList = packageList.filter(
        (i: Package) => i.packageID !== p.packageID
      );
      dispatch(updateSelectedPackages(filteredPackageList));
    } else {
      /*Clear packages of the same subject and different age group of the selected package*/
      const filteredPackageList = packageList.filter(
        (i: Package) => i.subject !== p.subject
      );
      dispatch(updateSelectedPackages([...filteredPackageList, p]));
    }
  };

  const isAgeGroupSelected = (ageGroup: string) => {
    /*To define the age group container styles*/
    const ageGroupPackage = selectedPackages.find(
      (i: Package) => i.ageGroup === ageGroup
    );
    return ageGroupPackage ? true : false;
  };

  const calculatePrice = (vatRate: number) => {
    //total without vat
    const price = selectedPackages.reduce(function (prev, cur) {
      return prev + cur.price;
    }, 0);

    const VATValue = price * (vatRate / 100);

    const priceWithVAT = price + VATValue;

    //----- Multi package discount -----///
    const multiPackageDiscountedTotalPriceWithoutVat = selectedPackages.reduce(
      function (prev, cur) {
        return prev + cur.price - cur.multiPurchaseDiscount;
      },
      0
    );
    const multiPackageDiscountedTotalPriceWithVat = selectedPackages.reduce(
      function (prev, cur) {
        return (
          prev +
          getVATAndPriceWithVAT(cur.price - cur.multiPurchaseDiscount, vatRate)
            .priceWithVAT
        );
      },
      0
    );

    const multiPackageVATValue = selectedPackages.reduce(function (prev, cur) {
      return prev + (cur.price - cur.multiPurchaseDiscount) * (vatRate / 100);
    }, 0);

    const multiPackageDiscount =
      price - multiPackageDiscountedTotalPriceWithoutVat;
    //------------------------------------//

    //------- Percentage of discount -------- //

    const percentageOffDiscountedTotalPriceWithoutVat = selectedPackages.reduce(
      function (prev, cur) {
        return prev + cur.price - cur.promoCodeDiscount;
      },
      0
    );
    const percentageOffDiscountedTotalPriceWithVat = selectedPackages.reduce(
      function (prev, cur) {
        return (
          prev +
          getVATAndPriceWithVAT(cur.price - cur.promoCodeDiscount, vatRate)
            .priceWithVAT
        );
      },
      0
    );

    const percentageOffVATValue = selectedPackages.reduce(function (prev, cur) {
      return prev + (cur.price - cur.promoCodeDiscount) * (vatRate / 100);
    }, 0);

    const percentageOffDiscount =
      price - percentageOffDiscountedTotalPriceWithoutVat;

    //------------------------------------//

    //------- Amount of discount -------- //
    const accumulatedDiscounts = selectedPackages.reduce(function (prev, cur) {
      return prev + cur.promoCodeDiscount;
    }, 0);

    let amountOffDiscountedTotalPriceWithoutVat;
    if (amountOffDiscount && accumulatedDiscounts > amountOffDiscount) {
      amountOffDiscountedTotalPriceWithoutVat = price - amountOffDiscount;
    } else {
      amountOffDiscountedTotalPriceWithoutVat = selectedPackages.reduce(
        function (prev, cur) {
          return prev + (cur.price - cur.promoCodeDiscount);
        },
        0
      );
    }

    const amountOffDiscountedTotalPriceWithVAT =
      amountOffDiscountedTotalPriceWithoutVat * 1.2;

    const amountOffDiscountValue = amountOffDiscount;

    const amountOffVATValue =
      amountOffDiscountedTotalPriceWithVAT -
      amountOffDiscountedTotalPriceWithoutVat;

    //------------------------------------//

    //------- Select the right discount-------- //

    const getSelectedPromoCodeDiscount = () => {
      if (amountOffDiscount) {
        return [
          amountOffDiscountedTotalPriceWithVAT,
          amountOffVATValue,
          amountOffDiscountValue,
        ];
      } else {
        return [
          percentageOffDiscountedTotalPriceWithVat,
          percentageOffVATValue,
          percentageOffDiscount,
        ];
      }
    };

    const [
      discountedTotalPriceWithVAT,
      discountedVATValue,
      discountValue,
    ]: any = getSelectedPromoCodeDiscount();

    //------- Final Discount Value -------- //
    const getDiscountedPrice = () => {
      if (selectedPackages.length > 1) {
        if (
          multiPackageDiscountedTotalPriceWithVat <= discountedTotalPriceWithVAT
        ) {
          return [
            multiPackageDiscountedTotalPriceWithVat,
            multiPackageVATValue,
            multiPackageDiscount,
          ];
        }
        if (
          multiPackageDiscountedTotalPriceWithVat > discountedTotalPriceWithVAT
        ) {
          return [
            discountedTotalPriceWithVAT,
            discountedVATValue,
            discountValue,
          ];
        }
      } else {
        return [discountedTotalPriceWithVAT, discountedVATValue, discountValue];
      }
    };

    const [
      finalDiscountTotalPriceWithVAT,
      finalDiscountedVATValue,
      finalDiscountValue,
    ]: any = getDiscountedPrice();

    //------------------------------------//

    //------- Final Price -------- //
    const finalPrice =
      finalDiscountTotalPriceWithVAT &&
      finalDiscountTotalPriceWithVAT < priceWithVAT
        ? finalDiscountTotalPriceWithVAT
        : priceWithVAT;

    const finalVATValue =
      finalDiscountTotalPriceWithVAT &&
      finalDiscountTotalPriceWithVAT < priceWithVAT
        ? finalDiscountedVATValue
        : VATValue;

    const finalDiscount =
      finalDiscountTotalPriceWithVAT &&
      finalDiscountTotalPriceWithVAT < priceWithVAT
        ? finalDiscountValue
        : 0;

    const finalDiscountType: string =
      selectedPackages.length > 1
        ? multiPackageDiscountedTotalPriceWithVat < discountedTotalPriceWithVAT
          ? 'Multipack'
          : 'Promo code'
        : 'Promo code';

    //------------------------------------//

    return [
      formatNumber(price),
      formatNumber(finalPrice),
      formatNumber(finalVATValue),
      formatNumber(finalDiscount),
      finalDiscountType,
    ];
  };

  const updateSelectedPackagesWithDiscount = (packages: any) => {
    const allUpdatedPackages = packages
      .map((p: any) =>
        p.subjects.map((i: any) =>
          i.subscriptions.map((j: any) => ({
            ...j,
            ageGroup: p.name,
            subject: i.name,
          }))
        )
      )
      .flat()
      .flat()
      .flat();

    const updatedSelectedPackages = selectedPackages
      .map((p: Package) => {
        return allUpdatedPackages.filter(
          (i: Package) => p.packageID === i.packageID
        );
      })
      .flat();
    updatedSelectedPackages.length > 0 &&
      dispatch(updateSelectedPackages(updatedSelectedPackages));
  };

  const applyPromoCode = async (vatRate: number) => {
    let result;
    if (AppPath.SUBSCRIBE === pathname) {
      result = await SubscribeApi.getSubscriptionsWithPromocode(
        promoCode.value
      );
    } else {
      result = await NewSubscriptionApi.checkPromoCode(promoCode.value);
    }

    if (result.data) {
      setPromoCode({
        value: '',
        error: '',
      });
      setAmountOffDiscount(result.data.amountOff);
      setAllPackages(result.data.generalPackages);
      updateSelectedPackagesWithDiscount(result.data.generalPackages);
      dispatch(setValidPromoCode(promoCode.value));
    }

    if (result.error) {
      setPromoCode({ value: '', error: result.error.message });
    }
  };

  const onContinue = () => {
    if (!vatRate) return;

    dispatch(
      setPrice({
        price: priceInfo.price,
        vat: priceInfo.vat,
        total: priceInfo.total,
        vatRate,
        discount: priceInfo.discount,
        discountType: priceInfo.appliedDiscountType,
      })
    );
    onSubmit();
  };

  if (error) {
    return <ErrorMessage message={ERROR_MESSAGE}></ErrorMessage>;
  }

  if (allPackages.length === 0 && !error) {
    return (
      <div
        style={{
          minHeight: ' calc(100vh - 300px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Spinner />
      </div>
    );
  }

  return (
    <div className="ChoosePackageForm">
      <div className="PackagesContainer">
        {allPackages.map((packageGroup: PackageGroup, i: number) => (
          <div
            key={i}
            className={`PackageOptions ${
              isAgeGroupSelected(packageGroup.name) ? 'Selected' : 'Unselected'
            }
              `}
          >
            <h2>{packageGroup.name}</h2>
            <p>{packageGroup.description + ' '}</p>
            {packageGroup &&
              packageGroup.subjects.map(
                (subjectInfo: Subjects, index: number) => (
                  <PackageCard
                    key={index}
                    subjectInfo={subjectInfo}
                    selectPackage={selectPackage}
                    selectedPackages={selectedPackages}
                    ageGroup={packageGroup.name}
                  />
                )
              )}
          </div>
        ))}
      </div>
      <div className="TotalDetails">
        <div className="DiscoundContainer">
          <input
            className="Discount"
            type="text"
            onChange={(e) =>
              setPromoCode((prevState) => ({
                ...prevState,
                value: e.target.value,
              }))
            }
            value={promoCode.value}
            placeholder="Enter promo code (optional)"
          />

          <button
            className="Btn BtnOrange"
            type="button"
            disabled={promoCode.value && vatRate ? false : true}
            onClick={() => vatRate && applyPromoCode(vatRate)}
          >
            Apply
          </button>
        </div>

        <FormGeneralError message={promoCode.error} />
        <div className="DetailsItem">
          <p>Price</p>
          <span>£ {formatPrice(priceInfo.price)}</span>
        </div>
        {priceInfo.discount > 0 && selectedPackages.length > 0 && (
          <div className="DetailsItem">
            <p>Discount ({priceInfo.appliedDiscountType})</p>
            <span>-£ {formatPrice(priceInfo.discount)}</span>
          </div>
        )}
        {priceInfo.discount > 0 && selectedPackages.length > 0 && (
          <div className="DetailsItem">
            <p>Discounted price</p>
            <span>£ {formatPrice(priceInfo.price - priceInfo.discount)}</span>
          </div>
        )}
        <div className="DetailsItem">
          <p>VAT ({vatRate}%)</p>
          <span>£ {formatPrice(priceInfo.vat)}</span>
        </div>

        <div className="DetailsItem">
          <p>Total</p>
          <span>£ {formatPrice(priceInfo.total)}</span>
        </div>
        <button
          className="Btn BtnOrange"
          type="submit"
          onClick={onContinue}
          disabled={selectedPackages.length === 0 || priceInfo?.price === 0}
        >
          Continue
        </button>
      </div>
    </div>
  );
};
